package diana.soleil.design.iraniancanadienbazzar.feature.dashboard

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.ListingMetricsRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.OwnerRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ReviewRepository
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingCounters
import diana.soleil.design.iraniancanadienbazzar.core.model.Review
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

/**
 * Dashboard overview (Stitch `f0c09108`) — **honest metrics only**: listing/review/rating figures are
 * real aggregates from the owner's listings; views/leads come from the server-maintained
 * `businesses/{id}/metrics/counters` docs (recordListingMetric Function is the only writer).
 */
data class DashboardUiState(
    val loading: Boolean = true,
    val businesses: List<Business> = emptyList(),
    /** Summed engagement across all owned listings; null until the counters stream emits. */
    val counters: ListingCounters? = null,
    val error: Boolean = false,
) {
    val listingCount: Int get() = businesses.size
    val pendingCount: Int get() = businesses.count { it.status.name.lowercase() == "pending" }
    val totalReviews: Int get() = businesses.sumOf { it.reviewCount }
    val averageRating: Double
        get() {
            val rated = businesses.filter { it.reviewCount > 0 }
            if (rated.isEmpty()) return 0.0
            return rated.sumOf { it.rating * it.reviewCount } / rated.sumOf { it.reviewCount }
        }
}

/** Streams the owner's listings for the overview + My Listings (Stitch `64546172`). */
@OptIn(ExperimentalCoroutinesApi::class)
class DashboardViewModel(
    ownerRepository: OwnerRepository,
    listingMetrics: ListingMetricsRepository,
) : ViewModel() {

    private val retryTrigger = MutableStateFlow(0)

    private val owned = retryTrigger.flatMapLatest { ownerRepository.observeOwnedBusinesses() }

    /** Re-subscribes the owned-listings stream after a load failure. */
    fun retry() {
        retryTrigger.value++
    }

    /** One live sum across every owned listing's counters doc (each is owner-readable per rules). */
    private val counters: Flow<ListingCounters?> = owned.flatMapLatest { result ->
        val ids = (result as? DataResult.Success)?.data?.map { it.id }.orEmpty()
        if (ids.isEmpty()) {
            flowOf(null)
        } else {
            combine(ids.map { listingMetrics.observeCounters(it) }) { perListing ->
                perListing.fold(ListingCounters()) { acc, r ->
                    val c = (r as? DataResult.Success)?.data ?: ListingCounters()
                    ListingCounters(
                        views = acc.views + c.views,
                        calls = acc.calls + c.calls,
                        directions = acc.directions + c.directions,
                        chats = acc.chats + c.chats,
                        shares = acc.shares + c.shares,
                    )
                }
            }
        }
    }

    val state: StateFlow<DashboardUiState> =
        combine(owned, counters) { result, sums ->
            when (result) {
                is DataResult.Success ->
                    DashboardUiState(loading = false, businesses = result.data, counters = sums)
                is DataResult.Failure ->
                    // Unauthorized (guest) reads as "owns nothing" — entry points are hidden
                    // anyway. Every OTHER failure (offline, backend) must NOT masquerade as an
                    // empty dashboard: an owner seeing "you have no listings" reads it as data loss.
                    if (result.error is AppError.Unauthorized) {
                        DashboardUiState(loading = false, businesses = emptyList())
                    } else {
                        DashboardUiState(loading = false, error = true)
                    }
            }
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), DashboardUiState())
}

/** Manage Reviews (Stitch `953ead76`) for one owned business: list + owner replies. */
data class ManageReviewsUiState(
    val loading: Boolean = true,
    val reviews: List<Review> = emptyList(),
    val error: Boolean = false,
    /** Review ids with an in-flight reply write. */
    val busyIds: Set<String> = emptySet(),
    /** Review ids whose last reply attempt failed — the row shows an inline error until retried. */
    val failedIds: Set<String> = emptySet(),
) {
    val averageRating: Double
        get() = if (reviews.isEmpty()) 0.0 else reviews.sumOf { it.rating }.toDouble() / reviews.size
}

@OptIn(ExperimentalCoroutinesApi::class)
class ManageReviewsViewModel(
    reviewRepository: ReviewRepository,
    private val ownerRepository: OwnerRepository,
    private val businessId: String,
) : ViewModel() {

    private val busyIds = MutableStateFlow<Set<String>>(emptySet())
    private val failedIds = MutableStateFlow<Set<String>>(emptySet())

    val state: StateFlow<ManageReviewsUiState> =
        combine(reviewRepository.observeVisibleReviews(businessId), busyIds, failedIds) { result, busy, failed ->
            when (result) {
                is DataResult.Success ->
                    ManageReviewsUiState(loading = false, reviews = result.data, busyIds = busy, failedIds = failed)
                is DataResult.Failure -> ManageReviewsUiState(loading = false, error = true)
            }
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), ManageReviewsUiState())

    fun reply(reviewId: String, text: String) {
        if (text.isBlank()) return
        viewModelScope.launch {
            busyIds.value += reviewId
            failedIds.value -= reviewId
            val result = ownerRepository.replyToReview(reviewId, text)
            // Success streams back through the reviews listener; a dropped failure looked
            // identical to success from the owner's chair — flag it on the row instead.
            if (result is DataResult.Failure) failedIds.value += reviewId
            busyIds.value -= reviewId
        }
    }
}
